package com.example.event_management_system.ui.screens.login

import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import com.example.event_management_system.data.models.UserRole
import com.example.event_management_system.data.repository.IAuthRepository
import com.example.event_management_system.data.repository.IUserRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class LoginState(
    val email: String = "",
    val password: String = "",
    val isLoading: Boolean = false,
    val error: String? = null,
    val isLoggedIn: Boolean = false,
    val initialLocationId: String? = null,
    val needsLocationPick: Boolean = false,
    val showForgotPassword: Boolean = false,
    val forgotPasswordEmail: String = "",
    val forgotPasswordSent: Boolean = false
)

class LoginScreenModel(
    private val authRepository: IAuthRepository,
    private val userRepository: IUserRepository
) : ScreenModel {

    private val _state = MutableStateFlow(LoginState())
    val state: StateFlow<LoginState> = _state.asStateFlow()

    init {
        checkAuthState()
    }

    private fun checkAuthState() {
        if (authRepository.isLoggedIn) {
            _state.update { it.copy(isLoggedIn = true) }
        }
    }

    fun onEmailChange(email: String) {
        _state.update { it.copy(email = email, error = null) }
    }

    fun onPasswordChange(password: String) {
        _state.update { it.copy(password = password, error = null) }
    }

    fun onForgotPasswordEmailChange(email: String) {
        _state.update { it.copy(forgotPasswordEmail = email) }
    }

    fun showForgotPassword() {
        _state.update { it.copy(showForgotPassword = true, forgotPasswordEmail = it.email) }
    }

    fun hideForgotPassword() {
        _state.update { it.copy(showForgotPassword = false, forgotPasswordSent = false) }
    }

    fun signIn() {
        val currentState = _state.value
        if (currentState.email.isBlank() || currentState.password.isBlank()) {
            _state.update { it.copy(error = "Please enter email and password") }
            return
        }

        screenModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            try {
                val result = authRepository.signIn(currentState.email, currentState.password)

                result.fold(
                    onSuccess = { userId ->
                        try {
                            val user = userRepository.getUser(userId)
                            if (user != null && user.isActive) {
                                try { userRepository.updateLastLogin(userId) } catch (_: Throwable) {}
                                val primaryId = user.primaryLocationId
                                when {
                                    primaryId != null -> _state.update {
                                        it.copy(isLoading = false, isLoggedIn = true, initialLocationId = primaryId)
                                    }
                                    user.locationIds.size == 1 -> _state.update {
                                        it.copy(isLoading = false, isLoggedIn = true, initialLocationId = user.locationIds.first())
                                    }
                                    else -> _state.update { it.copy(isLoading = false, needsLocationPick = true) }
                                }
                            } else {
                                authRepository.signOut()
                                _state.update {
                                    it.copy(
                                        isLoading = false,
                                        error = if (user != null) "Your account has been deactivated. Please contact your administrator."
                                                else "Account not configured. Please contact your administrator."
                                    )
                                }
                            }
                        } catch (e: Throwable) {
                            authRepository.signOut()
                            _state.update {
                                it.copy(isLoading = false, error = "Error loading account: ${e.message}")
                            }
                        }
                    },
                    onFailure = { exception ->
                        _state.update {
                            it.copy(
                                isLoading = false,
                                error = exception.message ?: "Sign in failed"
                            )
                        }
                    }
                )
            } catch (e: Throwable) {
                _state.update { it.copy(isLoading = false, error = e.message ?: "Sign in failed") }
            }
        }
    }

    fun sendPasswordReset() {
        val email = _state.value.forgotPasswordEmail
        if (email.isBlank()) {
            _state.update { it.copy(error = "Please enter your email") }
            return
        }

        screenModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            
            val result = authRepository.resetPassword(email)
            
            result.fold(
                onSuccess = {
                    _state.update { 
                        it.copy(isLoading = false, forgotPasswordSent = true) 
                    }
                },
                onFailure = { exception ->
                    _state.update { 
                        it.copy(
                            isLoading = false, 
                            error = exception.message ?: "Failed to send reset email"
                        ) 
                    }
                }
            )
        }
    }

    fun clearError() {
        _state.update { it.copy(error = null) }
    }
}
